import { Icon } from "@iconify/react";
import { motion, useInView } from "framer-motion";
import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

const Color = {
  themeBlue: "#4ea4f5",
  textPrimary: "#000",
  borderLight: "#e0e0e0",
  white: "#fff",
  bgLight: "#f8f9fa",
  pdfBlack: "#1a1a1a",
  audioBlue: "#4ea4f5",
  youtubeRed: "#ff0000",
  openai: "#000000",
  claude: "#d97706",
  gemini: "#4285f4",
  grok: "#000000",
};

interface FeatureSectionAIDemoProps {
  showDescription?: boolean;
  autoStart?: boolean;
}

const FeatureSectionAIDemo: React.FC<FeatureSectionAIDemoProps> = ({ showDescription = true, autoStart = false }) => {
  const { t } = useTranslation();

  const [showBulletPoints, setShowBulletPoints] = useState(autoStart);
  const [isAnimationPaused] = useState(false);

  const cardRef = useRef<HTMLDivElement>(null);
  const animationStartedRef = useRef<boolean>(false);
  const bulletPointsShownRef = useRef<boolean>(autoStart);

  const inViewResult = useInView(cardRef, { amount: 0.3 });
  const isInView = autoStart || inViewResult;

  // Show bullet points once in view
  if (isInView && !isAnimationPaused && !animationStartedRef.current) {
    animationStartedRef.current = true;
    if (!bulletPointsShownRef.current) {
      bulletPointsShownRef.current = true;
      // Use setTimeout to avoid setting state during render
      setTimeout(() => setShowBulletPoints(true), 600);
    }
  }

  // AI model pills
  const aiModels = [
    { icon: "simple-icons:openai", name: "ChatGPT", color: "#000000" },
    { icon: "simple-icons:googlegemini", name: "Gemini", color: "#4285f4" },
    { icon: "simple-icons:claude", name: "Claude", color: "#d97706" },
    { icon: "simple-icons:x", name: "Grok", color: "#000000" },
    { icon: "simple-icons:deepseek", name: "DeepSeek", color: "#4285f4" },
  ];

  // Generate output items for right panel
  const generateItems = [
    { icon: "lucide:file-text", label: "Summary", color: "#22c55e" },
    { icon: "lucide:list-todo", label: "Tasks", color: "#7c3aed" },
    { icon: "lucide:reply", label: "Replies", color: "#f97316" },
    { icon: "lucide:send", label: "Brief", color: "#eab308" },
    { icon: "lucide:route", label: "Follow-up", color: "#3b82f6" },
    { icon: "lucide:workflow", label: "Workflow", color: "#ec4899" },
    { icon: "lucide:grid-3x3", label: "Table", color: "#6366f1" },
    { icon: "lucide:message-circle", label: "Mobile", color: "#ef4444" },
    { icon: "lucide:file-check-2", label: "Report", color: "#14b8a6" },
  ];

  return (
    <section className={`${showDescription ? (autoStart ? 'py-4 md:py-8' : 'py-8 md:py-20') : 'py-0'} bg-white overflow-hidden`}>
      <div className={showDescription ? "container mx-auto px-4" : ""}>
        <div className={`${showDescription ? 'grid md:grid-cols-[45fr_55fr] gap-12 lg:gap-20 items-stretch max-w-7xl mx-auto' : 'flex justify-center'}`} style={showDescription ? { minHeight: "auto" } : undefined}>
          {/* Text Content - Left */}
          {showDescription && (
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              {...(autoStart ? { animate: { opacity: 1, x: 0 } } : { whileInView: { opacity: 1, x: 0 }, viewport: { once: true } })}
              transition={{ duration: 0.6 }}
              className="order-1 flex flex-col"
            >
              {/* Top group: badge + title + description */}
              <div>
                <div
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                  style={{ backgroundColor: `${Color.themeBlue}15` }}
                >
                  <Icon icon="lucide:sparkles" style={{ color: Color.themeBlue }} />
                  <span style={{ color: Color.themeBlue }} className="font-medium text-sm">
                    Workflow intelligence
                  </span>
                </div>

                <h2 className="text-[28px] md:text-[44px] lg:text-[56px] font-bold text-gray-900 mb-6 leading-tight mt-0">
                  Ask your work context anything
                </h2>

                <p className="text-lg md:text-xl leading-relaxed" style={{ color: "#726F6F" }}>
                  Pull decisions, tasks, owners, and next steps from the email, meetings, files, and messages your work already created.
                </p>
              </div>

              {/* Spacer — pushes bullets toward the bottom */}
              <div className="flex-1 min-h-[16px] max-h-[32px]" />

              {/* Bottom group: bullet list */}
              {/* Mobile bullets */}
              <div className="block md:hidden space-y-4">
                {[
                  { icon: "lucide:plug", text: "Connect the sources where work already happens" },
                  { icon: "lucide:cpu", text: "Use AI models with your workflow context" },
                  { icon: "lucide:message-square", text: "Keep source links beside every decision" },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${Color.themeBlue}15` }}
                    >
                      <Icon icon={item.icon} className="text-lg" style={{ color: Color.themeBlue }} />
                    </div>
                    <span className="text-base text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>

              {/* Desktop bullets - animated */}
              <div className="hidden md:block">
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: showBulletPoints ? 1 : 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {[
                    { icon: "lucide:plug", text: "Connect the sources where work already happens" },
                    { icon: "lucide:cpu", text: "Use AI models with your workflow context" },
                    { icon: "lucide:message-square", text: "Keep source links beside every decision" },
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: showBulletPoints ? 1 : 0, x: showBulletPoints ? 0 : -20 }}
                      transition={{ delay: showBulletPoints ? index * 0.15 : 0, duration: 0.4 }}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${Color.themeBlue}15` }}
                      >
                        <Icon icon={item.icon} className="text-lg" style={{ color: Color.themeBlue }} />
                      </div>
                      <span className="text-base text-gray-700">{item.text}</span>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* Visual Demo - Right — top aligns with title, bottom aligns with bullets */}
          <motion.div
            initial={{ opacity: 0, x: showDescription ? 50 : 0 }}
            {...(autoStart ? { animate: { opacity: 1, x: 0 } } : { whileInView: { opacity: 1, x: 0 }, viewport: { once: true } })}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-2 px-4 md:px-0 flex flex-col "
            style={{ paddingTop: "68px" }}
          >
            <div className="relative w-full max-w-[580px] mx-auto">
              {/* Floating badge - top left: 智能提问 */}
              <motion.div
                className="px-4 py-2 rounded-full shadow-lg flex items-center gap-2 absolute z-30"
                style={{ top: -6, left: -6, backgroundColor: Color.themeBlue }}
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Icon icon="lucide:sparkles" className="text-white" />
                <span className="text-white text-sm font-medium">{t("modern.aiAgent.badgeLeft", "智能提问")}</span>
              </motion.div>
              {/* Floating badge - top right: 智能输出 */}
              <motion.div
                className="px-4 py-2 rounded-full shadow-lg flex items-center gap-2 absolute z-30"
                style={{ top: -6, right: -6, backgroundColor: Color.themeBlue }}
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Icon icon="lucide:zap" className="text-white" />
                <span className="text-white text-sm font-medium">{t("modern.aiDemo.badge")}</span>
              </motion.div>

              {/* Main Card - 3-panel mock */}
              <div
                ref={cardRef}
                className="rounded-3xl overflow-hidden shadow-2xl relative"
                style={{
                  backgroundColor: Color.white,
                  border: `1px solid ${Color.borderLight}`,
                  aspectRatio: "580 / 400",
                }}
              >
                <div className="absolute inset-0 flex">
                  {/* ===== LEFT PANEL: AI Agent (50%) ===== */}
                  <div
                    className="flex flex-col border-r border-gray-200"
                    style={{ width: "50%", backgroundColor: Color.white }}
                  >
                    {/* Centered content group */}
                    <div className="flex-1 flex flex-col items-center justify-center px-4">
                      {/* Sparkle icon */}
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center mb-2"
                        style={{ backgroundColor: `${Color.themeBlue}15` }}
                      >
                        <Icon icon="lucide:sparkles" className="text-sm" style={{ color: Color.themeBlue }} />
                      </div>

                      {/* Title */}
                      <p className="text-[13px] font-bold text-gray-800 leading-tight text-center">
                        Workflow context Q&A
                      </p>
                      <p className="text-[9px] text-gray-400 mt-1 leading-snug italic text-center">
                        {t("modern.aiAgent.subtitle")}
                      </p>

                      {/* AI model pills */}
                      <div className="flex flex-wrap items-center justify-center gap-1.5 mt-4">
                        {aiModels.map((model, index) => (
                          <div
                            key={index}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                            style={{ border: "1.5px solid #d1d5db" }}
                          >
                            <Icon icon={model.icon} className="text-[12px]" style={{ color: model.color }} />
                            <span className="text-[10px] font-semibold text-gray-700">{model.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Chat input bar */}
                    <div className="px-3 pb-3">
                      <div
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl"
                        style={{ backgroundColor: "#f3f4f6", border: "1px solid #e5e7eb" }}
                      >
                        <span className="text-[10px] text-gray-400 flex-1 truncate">{t("modern.aiAgent.placeholder")}</span>
                        <div
                          className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: Color.themeBlue }}
                        >
                          <Icon icon="lucide:send" className="text-[9px] text-white" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ===== RIGHT PANEL: Generate/Output (50%) ===== */}
                  <div
                    className="flex flex-col"
                    style={{ width: "50%", backgroundColor: Color.bgLight }}
                  >
                    {/* Panel header */}
                    <div className="flex items-center gap-1.5 px-4 pt-4 pb-3">
                      <Icon icon="lucide:grid-2x2" className="text-xs text-gray-500" />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{t("modern.generate.title")}</span>
                    </div>

                    {/* 3x3 grid of generate items */}
                    <div className="grid grid-cols-3 gap-2 px-3 flex-1 content-start">
                      {generateItems.map((item, index) => (
                        <div
                          key={index}
                          className="flex flex-col items-center justify-center py-2.5 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                          style={{ border: "1px solid #eee" }}
                        >
                          <div
                            className="w-6 h-6 rounded-lg flex items-center justify-center mb-1"
                            style={{ backgroundColor: `${item.color}15` }}
                          >
                            <Icon icon={item.icon} className="text-[11px]" style={{ color: item.color }} />
                          </div>
                          <span className="text-[8px] font-medium text-gray-600 text-center leading-tight px-1">{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FeatureSectionAIDemo;
